import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypePostOrdersUploadParams, TypePostOrdersUploadResponse } from '~/types/delivery-agency.types';

const usePostOrdersUpload = () => {
  const { runAsync } = useAsyncHandler();

  const postOrdersUpload = useAPI<TypePostOrdersUploadParams, TypePostOrdersUploadResponse>({
    endpoint: API_ENDPOINT.DELIVERY_AGENCY.POST_ORDERS_UPLOAD,
    method: 'post',
    axiosConfig: {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  });

  const requestOrdersUpload = async (
    params: TypePostOrdersUploadParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypePostOrdersUploadResponse['data']) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => postOrdersUpload({ params }), {
      onSuccess: ({ data }) => onSuccess?.(data),
      onError: (error: TypeErrorResponseData['error']) => onError?.(error),
    });
  };

  return { requestOrdersUpload };
};

registerMockAPI<TypePostOrdersUploadResponse>({
  method: 'post',
  endpoint: '/api-delivery-agency/orders/upload',
  label: '배송대행/주문 엑셀 일괄 등록',
  scenarios: [
    {
      name: '정상 응답 — 전건 성공',
      response: { data: { totalCount: 3, successCount: 3, failCount: 0, failDetails: [] } },
    },
    {
      name: '전건 실패 — 당일배송 조건 미충족',
      response: {
        data: {
          totalCount: 3,
          successCount: 0,
          failCount: 3,
          failDetails: [
            {
              rowNums: [2],
              message:
                '현재 당일배송 서비스 이용 권한이 없습니다. 당일배송 서비스 이용을 원하시는 경우 영업 담당자에게 문의해 주세요.',
            },
            {
              rowNums: [3, 4],
              message: '당일배송 서비스는 낮 12시(정오) 이전 주문만 이용 가능합니다. 확인 후 재등록해주세요.',
            },
            { rowNums: [5], message: '당일배송 서비스 지역이 아닙니다. 확인 후 재등록해주세요.' },
          ],
        },
      },
    },
    {
      name: '일부 실패 — 긴 사유·링크 포함 (줄바꿈 확인용)',
      response: {
        data: {
          totalCount: 5,
          successCount: 3,
          failCount: 2,
          failDetails: [
            {
              rowNums: [2],
              message:
                '현재 당일배송 서비스 이용 권한이 없습니다. 당일배송 서비스 이용을 원하시는 경우 영업 담당자에게 문의해 주세요.',
            },
            {
              rowNums: [4],
              message:
                '엑셀 양식이 올바르지 않습니다. 최신 양식을 내려받아 다시 등록해 주세요. 양식 확인: https://kurly0521.atlassian.net/wiki/spaces/DPD/pages/4828627277 문의는 영업 담당자에게 부탁드립니다.',
            },
          ],
        },
      },
    },
  ],
});

export default usePostOrdersUpload;
